import { useCallback } from 'react';


import { getCombinedLinkFieldValueHref } from '@eneco/common/link';
import { keepCmsOverride, useRouter } from '@eneco/next';
import { LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';

export type QueryParams = Partial<Record<string, string | number | boolean | undefined | null>>;
export type LinkType = LinkFieldValue | string;
interface Props {
  link?: LinkType;
  queryParams?: QueryParams;
}

export const createQueryString = (queryParams?: QueryParams) => {
  if (!queryParams) {
    return '';
  }
  const params = new URLSearchParams();
  Object.entries(queryParams)
    .filter(([, value]) => value != null)
    .forEach(([key, value]) => {
      params.append(key, String(value));
    });
  return params.toString();
};
/**
 * `href` with `query` added: after the query string it may already carry, and before its anchor. A CMS
 * link's `href` is its whole URL, query string and anchor included (`@eneco/cms-types`'s `Link`), so
 * adding parameters cannot simply append a `?`.
 */
const withQuery = (href: string, query: string): string => {
  if (!query) return href;
  const hashAt = href.indexOf('#');
  const path = hashAt === -1 ? href : href.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  return `${path}${path.includes('?') ? '&' : '?'}${query}${hash}`;
};

export const useRedirect = () => {
  const router = useRouter();

  const redirect = useCallback(
    ({ queryParams, link }: Props) => {
      if (!link) return;
      const queryString = createQueryString(queryParams);
      // A redirect that stays on this site keeps the CMS the visitor is on, as a link rendered by `Link` does.
      if (typeof link === 'string') {
        window.location.href = keepCmsOverride(withQuery(link, queryString));
        return;
      }
      const ownQuery = (link.querystring ?? '').replace(/^\?/, '');
      const href = keepCmsOverride(
        withQuery(
          getCombinedLinkFieldValueHref({ ...link, querystring: '' }),
          [ownQuery, queryString].filter(Boolean).join('&'),
        ),
      );

      if (link.linktype === 'external') {
        window.location.href = href;
        return;
      }

      router.push(href);
    },
    [router],
  );

  return redirect;
};
